import { AnomalyEvent } from './types';
import { number, dateTime } from './helpers';

export default function EventChart({
  event,
  small = false,
}: {
  event: AnomalyEvent;
  small?: boolean;
}) {
  const points = event.points;
  if (points.length < 2)
    return <div className="anomaly-chart-empty">Ingen målekurve til denne hændelse</div>;
  const width = small ? 640 : 400,
    height = small ? 74 : 230;
  const inset = small ? 5 : 48;
  const minAt = points[0].at,
    maxAt = points[points.length - 1].at;
  const values = points
    .map((p) => p.value)
    .concat(
      event.expectedMin === null ? [] : [event.expectedMin],
      event.expectedMax === null ? [] : [event.expectedMax]
    );
  const min = Math.min(...values),
    max = Math.max(...values);
  const spread = Math.max(max - min, 1);
  const low = Math.max(0, min - spread * 0.15),
    high = max + spread * 0.15;
  const x = (at: number) =>
    inset + ((at - minAt) / Math.max(maxAt - minAt, 1)) * (width - inset * 2);
  const y = (value: number) =>
    height - inset - ((value - low) / (high - low)) * (height - inset * 2);
  const eventPoint = points.find((p) => p.at === event.occurredAt);
  return (
    <figure className={small ? 'anomaly-spark' : 'anomaly-chart'}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`${event.title}. ${number(points[0].value)} til ${number(
          points[points.length - 1].value
        )} ${event.unit}`}
      >
        {!small &&
          [0, 0.5, 1].map((fraction) => {
            const value = low + (high - low) * fraction;
            return (
              <g key={fraction}>
                <line
                  x1={inset}
                  x2={width - inset}
                  y1={y(value)}
                  y2={y(value)}
                  stroke="currentColor"
                  opacity=".1"
                />
                <text
                  x={inset - 7}
                  y={y(value) + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="currentColor"
                >
                  {number(value)}
                </text>
              </g>
            );
          })}
        {event.expectedMin !== null && event.expectedMax !== null && (
          <rect
            x={inset}
            width={width - inset * 2}
            y={y(event.expectedMax)}
            height={y(event.expectedMin) - y(event.expectedMax)}
            fill="#dbeafe"
            opacity=".7"
          />
        )}
        <polyline
          points={points.map((p) => `${x(p.at)},${y(p.value)}`).join(' ')}
          fill="none"
          stroke="#0284c7"
          strokeWidth={small ? 5 : 2.5}
          strokeLinejoin="round"
        />
        {eventPoint && (
          <g>
            <line
              x1={x(eventPoint.at)}
              x2={x(eventPoint.at)}
              y1={inset}
              y2={height - inset}
              stroke="#d97706"
              strokeDasharray="4 4"
            />
            <circle
              cx={x(eventPoint.at)}
              cy={y(eventPoint.value)}
              r={small ? 7 : 5}
              fill="#d97706"
              stroke="white"
              strokeWidth="2"
            />
          </g>
        )}
        {!small &&
          points.map((p) => (
            <circle key={p.at} cx={x(p.at)} cy={y(p.value)} r="3" fill="#0284c7">
              <title>
                {dateTime(p.at)} · {number(p.value)} {event.unit}
              </title>
            </circle>
          ))}
      </svg>
      {!small && (
        <>
          <figcaption>
            <span>{dateTime(minAt)}</span>
            <span>{event.unit}</span>
            <span>{dateTime(maxAt)}</span>
          </figcaption>
          {event.expectedMin !== null && (
            <p className="anomaly-chart-legend">
              <i /> Sædvanligt interval · <b /> Registreret afvigelse
            </p>
          )}
          <details className="anomaly-measurements">
            <summary>Vis målinger</summary>
            <table>
              <thead>
                <tr>
                  <th>Tidspunkt</th>
                  <th>{event.unit}</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <tr key={p.at}>
                    <td>{dateTime(p.at)}</td>
                    <td>{number(p.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      )}
    </figure>
  );
}
